/**
 * Formatowanie opisu zadania — zamiana w obie strony między tekstem
 * zapisanym w bazie a drzewem, które widzi człowiek.
 *
 * Dlaczego w bazie leży tekst, a nie HTML:
 *
 * 1. **Kontrakt `/api/v1` nie pozwala zmienić znaczenia pola.** `description`
 *    było i zostaje tekstem. Opis napisany przed wprowadzeniem formatowania
 *    czyta się dalej tak samo, a klient, który nie zna tych znaków, dostaje
 *    treść, a nie znaczniki.
 * 2. **HTML z przeglądarki trzeba by odkażać.** Zapisany tekst nie da się
 *    wstrzyknąć do strony: renderujemy go przez `React.createElement`,
 *    nigdy przez `dangerouslySetInnerHTML`. Jedyne HTML, jakie powstaje,
 *    powstaje tutaj, z rozpoznanych bloków.
 *
 * Składnia jest podzbiorem markdowna, żeby ten sam tekst dało się czytać
 * także tam, gdzie nikt go nie renderuje — w mailu albo w konsoli:
 *
 *     # Nagłówek        ## Podtytuł        - punkt listy
 *     **pogrubienie**   *kursywa*          __podkreślenie__   ~~przekreślenie~~
 */

export type Mark = "bold" | "italic" | "underline" | "strike";

export type Span = {
  text: string;
  marks: Mark[];
};

export type Block =
  | { kind: "h1" | "h2" | "p"; spans: Span[] }
  | { kind: "ul"; items: Span[][] };

/** Kolejność jest ustalona, żeby zapis tego samego tekstu zawsze wyglądał tak samo. */
const TOKENS: { token: string; mark: Mark }[] = [
  { token: "**", mark: "bold" },
  { token: "__", mark: "underline" },
  { token: "~~", mark: "strike" },
  { token: "*", mark: "italic" },
];

const ESCAPABLE = new Set(["*", "_", "~", "\\", "#", "-"]);

/* ── Tekst → bloki ────────────────────────────────────────────────────────── */

export function parse(source: string | null | undefined): Block[] {
  const lines = (source ?? "").replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const bullet = /^[-*•]\s+(.*)$/.exec(trimmed);
    if (bullet) {
      const item = inline(bullet[1] ?? "");
      const last = blocks[blocks.length - 1];
      // Kolejne punkty sklejają się w jedną listę — inaczej każdy dostałby
      // własny odstęp i lista rozpadłaby się na osobne akapity.
      if (last && last.kind === "ul") last.items.push(item);
      else blocks.push({ kind: "ul", items: [item] });
      continue;
    }

    const heading = /^(#{1,2})\s+(.*)$/.exec(trimmed);
    if (heading) {
      blocks.push({
        kind: heading[1]?.length === 1 ? "h1" : "h2",
        spans: inline(heading[2] ?? ""),
      });
      continue;
    }

    blocks.push({ kind: "p", spans: inline(trimmed) });
  }

  return blocks;
}

/**
 * Tekst w jednej linii na fragmenty z wyróżnieniami.
 *
 * Znacznik otwierający musi mieć po sobie znak inny niż spacja, a zamykający
 * przed sobą — bez tej reguły „3 * 4 * 5" zamieniałoby się w kursywę.
 */
export function inline(text: string, marks: Mark[] = []): Span[] {
  const spans: Span[] = [];
  let buffer = "";

  const flush = () => {
    if (buffer) spans.push({ text: buffer, marks: [...marks] });
    buffer = "";
  };

  let index = 0;
  while (index < text.length) {
    const char = text[index] ?? "";

    if (char === "\\" && ESCAPABLE.has(text[index + 1] ?? "")) {
      buffer += text[index + 1] ?? "";
      index += 2;
      continue;
    }

    const found = TOKENS.find(
      ({ token, mark }) =>
        !marks.includes(mark) &&
        text.startsWith(token, index) &&
        !/\s/.test(text[index + token.length] ?? " "),
    );
    if (found) {
      const close = closingAt(text, index + found.token.length, found.token);
      if (close > -1) {
        flush();
        spans.push(...inline(text.slice(index + found.token.length, close), [...marks, found.mark]));
        index = close + found.token.length;
        continue;
      }
    }

    buffer += char;
    index += 1;
  }

  flush();
  return spans;
}

function closingAt(text: string, from: number, token: string): number {
  for (let index = from; index <= text.length - token.length; index += 1) {
    if (text[index] === "\\") {
      index += 1;
      continue;
    }
    if (text.startsWith(token, index) && !/\s/.test(text[index - 1] ?? " ")) return index;
  }
  return -1;
}

/* ── Bloki → HTML (dla pola edycji) ───────────────────────────────────────── */

/**
 * HTML powstaje wyłącznie z rozpoznanych bloków, a każdy fragment tekstu
 * przechodzi przez `escape`. Do pola edycji nie trafia ani jeden znak
 * znacznika pochodzący z zapisanej treści.
 */
export function toHtml(source: string | null | undefined): string {
  const blocks = parse(source);
  if (!blocks.length) return "<p><br></p>";

  return blocks
    .map((block) => {
      if (block.kind === "ul") {
        return `<ul>${block.items.map((item) => `<li>${spansToHtml(item)}</li>`).join("")}</ul>`;
      }
      return `<${block.kind}>${spansToHtml(block.spans) || "<br>"}</${block.kind}>`;
    })
    .join("");
}

function spansToHtml(spans: Span[]): string {
  return spans
    .map(({ text, marks }) => {
      let html = escape(text);
      if (marks.includes("italic")) html = `<em>${html}</em>`;
      if (marks.includes("strike")) html = `<s>${html}</s>`;
      if (marks.includes("underline")) html = `<u>${html}</u>`;
      if (marks.includes("bold")) html = `<strong>${html}</strong>`;
      return html;
    })
    .join("");
}

function escape(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/* ── HTML → tekst (zapis po edycji) ───────────────────────────────────────── */

const MARK_TAGS: Record<string, Mark> = {
  STRONG: "bold",
  B: "bold",
  EM: "italic",
  I: "italic",
  U: "underline",
  S: "strike",
  STRIKE: "strike",
  DEL: "strike",
};

/**
 * Serializacja tego, co zostawiła przeglądarka w polu edycji.
 *
 * Bierzemy pod uwagę wyłącznie znane znaczniki — reszta (a przeglądarki
 * potrafią dosypać własne `span`-y i style) jest przezroczysta i zostaje po
 * niej sam tekst. Dzięki temu zapis nigdy nie wnosi do bazy niczego, czego
 * ten moduł nie umiałby odczytać z powrotem.
 */
export function fromHtml(root: HTMLElement): string {
  const lines: string[] = [];

  const readBlock = (node: Node, prefix = ""): void => {
    const runs: Span[] = [];
    collect(node, [], runs);
    const text = runsToMarkdown(runs).trim();
    if (text || prefix) lines.push(`${prefix}${text}`);
  };

  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? "";
        if (text.trim()) lines.push(escapeText(text.trim()));
        continue;
      }
      if (!(child instanceof HTMLElement)) continue;

      switch (child.tagName) {
        case "H1":
          readBlock(child, "# ");
          break;
        case "H2":
          readBlock(child, "## ");
          break;
        case "UL":
        case "OL":
          for (const item of Array.from(child.children)) readBlock(item, "- ");
          break;
        case "BR":
          lines.push("");
          break;
        case "P":
        case "DIV":
        case "SECTION":
        case "BLOCKQUOTE":
        default:
          // Zagnieżdżone bloki (przeglądarki potrafią owinąć akapity w `div`)
          // rozwijamy dalej — inaczej trzy akapity skleiłyby się w jeden.
          if (hasBlockChildren(child)) walk(child);
          else readBlock(child);
      }
    }
  };

  walk(root);

  // Puste linie na końcu i zdublowane w środku nie niosą treści, a psują
  // porównanie „czy opis się zmienił".
  return lines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const BLOCK_TAGS = new Set(["P", "DIV", "SECTION", "BLOCKQUOTE", "UL", "OL", "H1", "H2"]);

function hasBlockChildren(element: HTMLElement): boolean {
  return Array.from(element.children).some((child) => BLOCK_TAGS.has(child.tagName));
}

function collect(node: Node, marks: Mark[], runs: Span[]): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = (child.textContent ?? "").replace(/\u00a0/g, " ");
      if (text) runs.push({ text, marks: [...marks] });
      continue;
    }
    if (!(child instanceof HTMLElement)) continue;
    if (child.tagName === "BR") {
      runs.push({ text: " ", marks: [] });
      continue;
    }
    const mark = MARK_TAGS[child.tagName];
    collect(child, mark && !marks.includes(mark) ? [...marks, mark] : marks, runs);
  }
}

function runsToMarkdown(runs: Span[]): string {
  const merged: Span[] = [];
  for (const run of runs) {
    const last = merged[merged.length - 1];
    if (last && sameMarks(last.marks, run.marks)) last.text += run.text;
    else merged.push({ text: run.text, marks: run.marks });
  }

  return merged
    .map(({ text, marks }) => {
      // Wyróżnienie nie obejmuje spacji na krawędziach — „**tekst **" nie jest
      // pogrubieniem dla żadnego parsera, w tym dla naszego.
      const [, before = "", core = "", after = ""] = /^(\s*)([\s\S]*?)(\s*)$/.exec(text) ?? [];
      if (!core) return text;

      let out = escapeText(core);
      if (marks.includes("italic")) out = `*${out}*`;
      if (marks.includes("strike")) out = `~~${out}~~`;
      if (marks.includes("underline")) out = `__${out}__`;
      if (marks.includes("bold")) out = `**${out}**`;
      return `${before}${out}${after}`;
    })
    .join("");
}

function sameMarks(a: Mark[], b: Mark[]): boolean {
  return a.length === b.length && a.every((mark) => b.includes(mark));
}

/** Znaki składni w tekście pisanym przez człowieka zostają tekstem. */
function escapeText(text: string): string {
  return text.replace(/\\/g, "\\\\").replace(/([*_~])/g, "\\$1").replace(/^([#-])/, "\\$1");
}

/** Czy opis niesie cokolwiek poza białymi znakami. */
export function isEmpty(source: string | null | undefined): boolean {
  return parse(source).length === 0;
}
